
class MCartDrawer extends HTMLElement {
  constructor() {
    super();

    this.cartDrawerInner = this.querySelector(".m-cart-drawer__inner");
    this.cartDrawerCloseIcon = this.querySelector(".m-cart-drawer__close");
    this.cartOverlay = this.querySelector(".m-cart__overlay");
    this.rootUrl = window.Shopify.routes.root;

    this.setHeaderCartIconAccessibility();

    this.cartDrawerCloseIcon.addEventListener(
      "click",
      this.close.bind(this)
    );

    this.addEventListener("click", (event) => {
      if (
        event.target.closest(".m-cart-drawer__inner") !==
        this.cartDrawerInner
      ) {
        this.close();
      }
    });
  }

  setHeaderCartIconAccessibility() {
    const cartLinks = document.querySelectorAll(".m-cart-icon-bubble");

    cartLinks.forEach((cartLink) => {
      cartLink.setAttribute("role", "button");
      cartLink.setAttribute("aria-haspopup", "dialog");

      cartLink.addEventListener("click", (event) => {
        if (MinimogSettings.enable_cart_drawer) {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    });
  }

  open(triggeredBy) {
    if (triggeredBy) {
      this.setActiveElement(triggeredBy);
    }

    this.classList.add("m-cart-drawer--active");

    requestAnimationFrame(() => {
      this.style.setProperty("--m-bg-opacity", "0.5");
      this.cartDrawerInner.style.setProperty("--translate-x", "0");
    });

    window.MinimogEvents.emit(
      MinimogTheme.pubSubEvents.openCartDrawer
    );

    document.documentElement.classList.add("prevent-scroll");
  }

  close() {
    this.style.setProperty("--m-bg-opacity", "0");
    this.cartDrawerInner.style.setProperty("--translate-x", "100%");

    setTimeout(() => {
      this.classList.remove("m-cart-drawer--active");
      document.documentElement.classList.remove("prevent-scroll");
    }, 300);
  }

  renderContents(parsedState) {
    this.classList.remove("m-cart--empty");

    this.productId = parsedState.id;

    const sections = parsedState.sections || {};

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) {
        console.warn(
          "[Minimog Cart] Target element missing:",
          section.selector || section.id
        );
        return;
      }

      const newContent = this.getSectionInnerHTML(
        sections[section.id],
        section.selector
      );

      if (newContent !== null) {
        sectionElement.innerHTML = newContent;
      }
    });

    setTimeout(() => {
      this.open();
    });

    // FELT: Tell the shipping popup that the cart changed.
    this.notifyFeltShippingPopup();
  }

  updateCartCount(itemCount) {
    const cartCounts = document.querySelectorAll(
      ".m-cart-count-bubble"
    );

    cartCounts.forEach((cartCount) => {
      if (itemCount > 0) {
        cartCount.textContent = itemCount;
        cartCount.classList.remove("m:hidden");
      } else {
        cartCount.classList.add("m:hidden");
      }
    });
  }

  getCart() {
    return fetchJSON(this.rootUrl + "cart.json");
  }

  // FELT: Custom cart update notification.
  notifyFeltShippingPopup() {
    document.dispatchEvent(
      new CustomEvent("felt:cart-updated")
    );
  }

  onCartDrawerUpdate(updateFooter = true) {
    fetch(`${MinimogSettings.routes.cart}?section_id=cart-drawer`)
      .then((response) => {
        if (!response.ok) {
          throw new Error(
            "Cart section request failed: " + response.status
          );
        }

        return response.text();
      })
      .then((responseText) => {
        this.getSectionsToRender().forEach((section) => {
          if (section.block !== "cart-items" && !updateFooter) {
            return;
          }

          const sectionElement = section.selector
            ? document.querySelector(section.selector)
            : document.getElementById(section.id);

          if (!sectionElement) {
            console.warn(
              "[Minimog Cart] Target element missing:",
              section.selector || section.id
            );
            return;
          }

          const newContent = this.getSectionInnerHTML(
            responseText,
            section.selector
          );

          if (newContent !== null) {
            sectionElement.innerHTML = newContent;
          }
        });
      })
      .catch((error) => {
        console.error(
          "[Minimog Cart] Drawer refresh failed:",
          error
        );
      });

    this.getCart()
      .then((cart) => {
        this.classList.toggle(
          "m-cart--empty",
          cart.item_count === 0
        );

        this.updateCartCount(cart.item_count);

        // FELT: Cart data is refreshed.
        this.notifyFeltShippingPopup();
      })
      .catch((error) => {
        console.error(
          "[Minimog Cart] Cart count refresh failed:",
          error
        );
      });
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    if (typeof html !== "string") {
      console.warn(
        "[Minimog Cart] Missing or invalid section response:",
        selector,
        html
      );
      return null;
    }

    const documentFragment = new DOMParser().parseFromString(
      html,
      "text/html"
    );

    const element = documentFragment.querySelector(selector);

    if (!element) {
      console.warn(
        "[Minimog Cart] Expected element not found:",
        selector,
        "Response preview:",
        html.slice(0, 500)
      );

      return null;
    }

    return element.innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "[data-minimog-cart-items]",
        block: "cart-items",
      },
      {
        id: "cart-drawer",
        selector: "[data-minimog-cart-discounts]",
        block: "cart-footer",
      },
      {
        id: "cart-drawer",
        selector: "[data-cart-subtotal]",
        block: "cart-footer",
      },
      {
        id: "cart-drawer",
        selector: "[data-minimog-gift-wrapping]",
        block: "cart-footer",
      },
    ];
  }

  getSectionDOM(html, selector = ".shopify-section") {
    if (typeof html !== "string") {
      return null;
    }

    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define("m-cart-drawer", MCartDrawer);

class MCartDrawerItems extends MCartTemplate {
  getSectionsToRender() {
    return [
      {
        id: "MinimogCartDrawer",
        section: "cart-drawer",
        selector: "[data-minimog-cart-items]",
      },
      {
        id: "MinimogCartDrawer",
        section: "cart-drawer",
        selector: "[data-minimog-cart-discounts]",
      },
      {
        id: "MinimogCartDrawer",
        section: "cart-drawer",
        selector: "[data-cart-subtotal]",
      },
      {
        id: "MinimogCartDrawer",
        section: "cart-drawer",
        selector: "[data-minimog-gift-wrapping]",
      },
    ];
  }
}

customElements.define(
  "m-cart-drawer-items",
  MCartDrawerItems
);
